import { apiRequest } from './core/client';
import { AccountSchema, parseArrayWithSchema, parseWithSchema } from './schemas';
import type { Account } from '@/types/email';

export type { Account };

// ============================================================================
// Account Config Type (extended details)
// ============================================================================

export interface AccountConfig {
  id: string;
  emailAddress: string;
  displayName: string | null;
  provider: string | null;
  status: Account['status'];
  hasCredentials: boolean;
  color: string | null;
  defaultSignatureId: string | null;
  imap: {
    host: string | null;
    port: number | null;
    security: string | null;
    username: string | null;
    allowInvalidCert: boolean;
  } | null;
  smtp: {
    host: string | null;
    port: number | null;
    security: string | null;
    username: string | null;
    from: string | null;
    fromName: string | null;
    allowInvalidCert: boolean;
  } | null;
  gmail: {
    email: string | null;
    name: string | null;
    fromName: string | null;
    picture: string | null;
    refreshTokenPresent: boolean;
    accessTokenExpiresAt: string | null;
  } | null;
}

// ============================================================================
// Account Fetching
// ============================================================================

export async function fetchAccounts(): Promise<Account[]> {
  const data = await apiRequest<unknown[]>({
    path: '/accounts',
  });
  return parseArrayWithSchema(AccountSchema, data, 'fetchAccounts') as unknown as Account[];
}

export async function fetchAccountConfig(accountId: string): Promise<AccountConfig> {
  const data = await apiRequest<unknown>({
    path: `/accounts/${encodeURIComponent(accountId)}`,
  });

  const record = data as Record<string, unknown>;

  const mapTransport = <T>(transport: unknown, mapper: (t: Record<string, unknown>) => T): T | null => {
    if (!transport || typeof transport !== 'object') return null;
    return mapper(transport as Record<string, unknown>);
  };

  return {
    id: typeof record.id === 'string' ? record.id : accountId,
    emailAddress: typeof record.emailAddress === 'string' ? record.emailAddress : '',
    displayName: typeof record.displayName === 'string' ? record.displayName : null,
    provider: typeof record.provider === 'string' ? record.provider : null,
    status: (typeof record.status === 'string' ? record.status : 'idle') as Account['status'],
    hasCredentials: Boolean(record.hasCredentials),
    color: typeof record.color === 'string' ? record.color : null,
    defaultSignatureId: typeof record.defaultSignatureId === 'string' ? record.defaultSignatureId : null,
    imap: mapTransport(record.imap, (t) => ({
      host: typeof t.host === 'string' ? t.host : null,
      port: typeof t.port === 'number' ? t.port : null,
      security: typeof t.security === 'string' ? t.security : null,
      username: typeof t.username === 'string' ? t.username : null,
      allowInvalidCert: Boolean(t.allowInvalidCert),
    })),
    smtp: mapTransport(record.smtp, (t) => ({
      host: typeof t.host === 'string' ? t.host : null,
      port: typeof t.port === 'number' ? t.port : null,
      security: typeof t.security === 'string' ? t.security : null,
      username: typeof t.username === 'string' ? t.username : null,
      from: typeof t.from === 'string' ? t.from : null,
      fromName: typeof t.fromName === 'string' ? t.fromName : null,
      allowInvalidCert: Boolean(t.allowInvalidCert),
    })),
    gmail: mapTransport(record.gmail, (t) => ({
      email: typeof t.email === 'string' ? t.email : null,
      name: typeof t.name === 'string' ? t.name : null,
      fromName: typeof t.fromName === 'string' ? t.fromName : null,
      picture: typeof t.picture === 'string' ? t.picture : null,
      refreshTokenPresent: Boolean(t.refreshTokenPresent),
      accessTokenExpiresAt: typeof t.accessTokenExpiresAt === 'string' ? t.accessTokenExpiresAt : null,
    })),
  };
}

// ============================================================================
// Account Management
// ============================================================================

export interface CreateAccountPayload {
  email: string;
  password?: string;
  provider?: string;
  displayName?: string | null;
  color?: string | null;
  defaultSignatureId?: string | null;
  settings?: Record<string, unknown>;
}

export interface UpdateAccountPayload {
  email?: string;
  password?: string;
  displayName?: string | null;
  provider?: string;
  color?: string | null;
  defaultSignatureId?: string | null;
  settings?: Record<string, unknown>;
}

export async function createAccount(payload: CreateAccountPayload): Promise<Account> {
  const data = await apiRequest<unknown>({
    path: '/accounts',
    method: 'POST',
    body: payload,
  });
  return parseWithSchema(AccountSchema, data, 'createAccount') as unknown as Account;
}

export async function updateAccount(
  accountId: string,
  payload: UpdateAccountPayload
): Promise<Account> {
  const data = await apiRequest<unknown>({
    path: `/accounts/${encodeURIComponent(accountId)}`,
    method: 'PUT',
    body: payload,
  });
  return parseWithSchema(AccountSchema, data, 'updateAccount') as unknown as Account;
}

// ============================================================================
// Gmail OAuth
// ============================================================================

export interface GmailOAuthStartResponse {
  url: string;
  state: string;
  expiresAt?: string | null;
}

export interface GmailOAuthStatusResponse {
  state: string;
  status: 'pending' | 'complete' | 'failed';
  email?: string | null;
  profile?: {
    email?: string | null;
    name?: string | null;
    picture?: string | null;
  } | null;
  error?: string | null;
}

export async function startGmailOAuth(
  options: { accountId?: string; mode?: 'create' | 'edit'; loginHint?: string } = {}
): Promise<GmailOAuthStartResponse> {
  const body: Record<string, unknown> = {};
  if (options.mode) body.mode = options.mode;
  if (options.accountId) body.accountId = options.accountId;
  if (options.loginHint) body.loginHint = options.loginHint;

  const data = await apiRequest<unknown>({
    path: '/providers/google/oauth/start',
    method: 'POST',
    body,
  });
  const record = data as Record<string, unknown>;
  return {
    url: typeof record.url === 'string' ? record.url : '',
    state: typeof record.state === 'string' ? record.state : '',
    expiresAt: typeof record.expiresAt === 'string' ? record.expiresAt : null,
  };
}

export async function fetchGmailOAuthStatus(state: string): Promise<GmailOAuthStatusResponse> {
  const data = await apiRequest<unknown>({
    path: `/providers/google/oauth/status/${encodeURIComponent(state)}`,
  });
  const record = data as Record<string, unknown>;
  const profileRecord =
    record.profile && typeof record.profile === 'object'
      ? (record.profile as Record<string, unknown>)
      : null;

  return {
    state: typeof record.state === 'string' ? record.state : state,
    status: (typeof record.status === 'string' ? record.status : 'pending') as GmailOAuthStatusResponse['status'],
    email: typeof record.email === 'string' ? record.email : null,
    profile: profileRecord
      ? {
          email: typeof profileRecord.email === 'string' ? profileRecord.email : null,
          name: typeof profileRecord.name === 'string' ? profileRecord.name : null,
          picture: typeof profileRecord.picture === 'string' ? profileRecord.picture : null,
        }
      : null,
    error: typeof record.error === 'string' ? record.error : null,
  };
}

// ============================================================================
// Account Sync
// ============================================================================

export async function triggerAccountSync(
  accountId: string,
  mailboxId?: string | null
): Promise<void> {
  const body = mailboxId ? { mailboxId } : undefined;
  await apiRequest({
    path: `/accounts/${encodeURIComponent(accountId)}/sync`,
    method: 'POST',
    body,
  });
}
